import { format } from 'date-fns';
import { FETCH_RECEIPTS_V2 } from '~/actions';
import { parseDateString } from '~/utils/DateUtils';
import {
  Box,
  Column,
  CustomSpinner,
  DataGrid,
  LineClamp,
  Row,
  ScrollArea,
  SortingArrow,
  Text,
} from '~/commonComponents';
import { EnforcePaymentHistoryTable } from '~/constants/ff.posthog';
import browserHistory from '~/browserHistory';
import { useLoading } from '~/hooks';
import { currencyFormat } from '~/hooks/currencyFormat';
import { ArrowRightIcon } from '~/images/icons';
import { IReceiptV2, ReceiptType } from '~/models';
import { Colors } from '~/themes/colors';
import { SortFields } from './enums';
import { useAllTransactionReceiptV2 } from './hooks';
import { ReceiptStatus } from './Status';
import { useRef } from 'react';
import { getShouldEnforcePermanentJobV2 } from '~/growthbook';

interface IProps {
  filters: {
    transactionDate: string;
  };
}

const getColors = (type: ReceiptType) => {
  switch (type) {
    case ReceiptType.ListingFee:
      return Colors.jobs_listing_fee_background;
    case ReceiptType.OnsiteInterview:
      return Colors.jobs_interview_background;
    default:
      return Colors.jobs_shift_background;
  }
};

const getText = (type: ReceiptType) => {
  switch (type) {
    case ReceiptType.ListingFee:
      return 'LISTING FEE';
    case ReceiptType.OnsiteInterview:
      return 'INTERVIEW';
    default:
      return 'SHIFT';
  }
};

export const ReceiptTableV2 = ({ filters }: IProps) => {
  const { receipts, scrollContainerRef, sort, onFetchNextPage, onSortChange } =
    useAllTransactionReceiptV2({ filters });
  const { isLoading } = useLoading([FETCH_RECEIPTS_V2]);

  const isEnforcePermanentJobV2 = useRef(getShouldEnforcePermanentJobV2()).current;

  return (
    <ScrollArea
      ref={scrollContainerRef}
      onEndReached={onFetchNextPage}
      style={{
        minHeight: 100,
      }}
    >
      <DataGrid
        columns={[
          {
            key: 'office',
            header: 'Office',
            render: (item) => (
              <Text data-cy="office-name-column" paddingRight={8}>
                <LineClamp lineClamp={2} text={item.officeName} />
              </Text>
            ),
          },
          ...(isEnforcePermanentJobV2
            ? [
                {
                  key: 'type',
                  header: 'Type',
                  width: '140px',
                  render: (item: IReceiptV2) => (
                    <Box
                      alignSelf="flex-start"
                      paddingVertical={2}
                      paddingHorizontal={10}
                      borderRadius={8}
                      backgroundColor={getColors(item.type)}
                    >
                      <Text color="neutral_500">{getText(item.type)}</Text>
                    </Box>
                  ),
                },
              ]
            : []),
          {
            key: 'transactionDate',
            header: (
              <Row gap={8}>
                <Text bold color={Colors.primary_500} fontSize={18}>
                  Transaction Date
                </Text>
                <SortingArrow
                  name={SortFields.TransactionDate}
                  sorting={{
                    direction: sort.order.toUpperCase() as 'ASC' | 'DESC',
                    field: sort.field,
                  }}
                  toggleOnly
                  onChange={(val) => onSortChange(val?.field as SortFields, val)}
                />
              </Row>
            ),
            width: '185px',
            render: (item) => (
              <Column data-cy="payment-date-column">
                <Text>{format(parseDateString(item.transactionDate), 'EEE, MMM dd, yyyy')}</Text>
                <Text>{format(new Date(item.transactionDate), 'hh:mm a')}</Text>
              </Column>
            ),
          },
          {
            key: 'shiftDate',
            header: (
              <Row gap={8}>
                <Text bold color={Colors.primary_500} fontSize={18}>
                  {isEnforcePermanentJobV2 ? 'Posting Date' : 'Shift Date'}
                </Text>
                <SortingArrow
                  name={SortFields.ShiftDate}
                  sorting={{
                    direction: sort.order.toUpperCase() as 'ASC' | 'DESC',
                    field: sort.field,
                  }}
                  onChange={(val) => onSortChange(val?.field as SortFields, val)}
                />
              </Row>
            ),
            width: '185px',
            render: (item) => (
              <Text data-cy="shift-date-column" data-testid="shift-date-column">
                {format(parseDateString(item.shiftDate), 'EEE, MMM dd, yyyy')}
              </Text>
            ),
          },
          {
            key: 'total',
            header: 'Total',
            width: '120px',
            render: (item) => {
              const isRefund = item.totalRefund > 0;

              return (
                <Text
                  bold
                  color={isRefund ? Colors.success_600 : Colors.neutral_600}
                  data-cy="payment-amount-column"
                  data-testid="payment-amount-column"
                  fontSize={15}
                >
                  <span>{isRefund ? '-' : ''}$</span>
                  <span>
                    {isRefund ? currencyFormat(item.totalRefund) : currencyFormat(item.totalCharge)}
                  </span>
                </Text>
              );
            },
          },
          {
            key: 'status',
            header: 'Status',
            width: '120px',
            render: (item) => (
              <Box data-cy="payment-status-column">
                <ReceiptStatus status={item.status} />
              </Box>
            ),
          },
          {
            key: 'action',
            header: '',
            width: '50px',
            align: 'center',
            render: () => <ArrowRightIcon />,
          },
        ]}
        data={receipts}
        dataCy={{
          header: 'payment-history-table',
          root: 'all-transactions-table',
          rowItem: 'payment-history-table-row',
        }}
        dataTestId={{
          header: 'payment-history-table',
          root: 'all-transactions-table',
          rowItem: 'payment-history-table-row',
        }}
        keyExtractor={(it) => `${it.officeId}.${it.shiftId}.${it.transactionDate}.${it.status}`}
        isStickyHeader
        onRowClick={(it) => {
          if (isEnforcePermanentJobV2 && it.type === ReceiptType.ListingFee && it.postingId) {
            browserHistory.push(`/permanent-jobs/job-overview/${it.postingId}`);
            return;
          }

          if (isEnforcePermanentJobV2 && it.type === ReceiptType.ListingFee) {
            browserHistory.push(`/permanent-jobs/postings`);
            return;
          }

          browserHistory.push(`job/${it.shiftId}/details`, {
            job_id: it.shiftId,
            jobId: it.shiftId,
          });
        }}
      />
      {isLoading && (
        <Box marginTop={24}>
          <CustomSpinner isLoading size={16} />
        </Box>
      )}
    </ScrollArea>
  );
};
